'use es6';

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import tinymce from 'tinymce';
import UIIconButton from 'UIComponents/button/UIIconButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIAbstractFloaty from 'UIComponents/tooltip/UIAbstractFloaty';
import { CONTENT_GENERATION_TEXT_SELECTED, CONTENT_GENERATION_INLINE_EDIT_INTERACTION, CONTENT_GENERATION_MENU_OPEN, CONTENT_GENERATION_SELECTION_COMMAND_ACTION_TYPE } from '../constants/usageTracking';
import { useAtomValue } from '../data/atomUtils';
import { autocompleteActiveRangeAtom } from '../data/autocompleteAtoms';
import withTinyMcePopoverLifecycle from '../hoc/withTinyMcePopoverLifecycle';
import { newlinesToParagraphs } from '../hscontentgeneration/utils';
import { isWhitespace } from '../utils';
import { getAnnotatedSelectionContextAsPlainText } from '../utils/dom';
import { trackInteraction } from '../utils/usageTracking';
import TinyMcePopover from './TinyMcePopover';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TinyMCEFloaty = withTinyMcePopoverLifecycle(UIAbstractFloaty);
const getBottomEditorSelectionBounds = editor => {
  const selection = editor.selection.getSel();
  // Handle cases where some browsers include no content in the next node for
  // triple clicks
  const hasNoContentInFocusNode = selection.anchorNode !== selection.focusNode && selection.focusOffset === 0;
  // Only get selection rects if there is an computable selection
  // otherwise return null
  if (!selection || selection.rangeCount === 0) {
    return null;
  }
  const selectionBounds = selection.getRangeAt(0).getClientRects();
  let index = selectionBounds.length - 1;
  if (hasNoContentInFocusNode) {
    const selectionEndNode = selection.focusNode;
    if (selectionEndNode.getBoundingClientRect) {
      const focusNodeBoundingRect = selectionEndNode.getBoundingClientRect();
      const lastRangeClientRect = selectionBounds[selectionBounds.length - 1];
      // If the last bounding rect in our range has the same bottom as the focus node
      // then we know that our last client rect has no selection content
      // Some browsers (like Chrome) include a focus node with no content, but don't include its
      // bounding rect in the client list
      const lastRangeClientRectIncludesNoContent = focusNodeBoundingRect.bottom === lastRangeClientRect.bottom;
      if (lastRangeClientRectIncludesNoContent) {
        index--;
      }
    }
  }
  index = Math.max(index, 0);
  return selectionBounds[index];
};
const useComputeSelectionFloatyPosition = editor => {
  const computePosition = useCallback(({
    contentDimensions
  }) => {
    const targetBounds = getBottomEditorSelectionBounds(editor);
    const rectCoords = {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0
    };
    if (!targetBounds) {
      return rectCoords;
    }
    const distance = 10;
    // Most of this was taken from UIComponent's computePopupPosition
    // https://git.hubteam.com/HubSpot/UIComponents/blob/369344c1bfcbf3d78f2436f6112aa6a60638c04d/UIComponents/static-3.3101/js/tooltip/utils/Placement.ts#L108
    rectCoords.right = targetBounds.right + distance + contentDimensions.width;
    rectCoords.left = rectCoords.right - contentDimensions.width;
    const targetMiddle = targetBounds.top + targetBounds.height / 2;
    rectCoords.top = targetMiddle - contentDimensions.height / 2;
    rectCoords.bottom = targetMiddle + contentDimensions.height / 2;
    const editorWindow = editor.getWin();
    if (editorWindow && editorWindow.document !== document) {
      // In this case, the tinymce editor has been rendered inside of a separate iframe
      // so we need to offset our position relative to the container of the iframe
      const container = editor.getContainer();
      if (!container) {
        return {};
      }
      const containerRect = container.getBoundingClientRect();
      rectCoords.top += containerRect.top;
      rectCoords.bottom += containerRect.bottom;
      rectCoords.left += containerRect.left;
      rectCoords.right += containerRect.right;
    }
    const offsetParent = document.body;
    const offsetParentRect = offsetParent.getBoundingClientRect();
    const bodyToPageOffset = offsetParentRect.top + document.documentElement.scrollTop;
    // Translating the rect by `bodyToPageOffset` in the y-direction means adding to both `top` and `bottom`
    rectCoords.top = rectCoords.top - offsetParentRect.top + bodyToPageOffset;
    rectCoords.bottom = rectCoords.bottom - offsetParentRect.top + bodyToPageOffset;
    rectCoords.left = rectCoords.left - offsetParentRect.left;
    rectCoords.right = rectCoords.right - offsetParentRect.left;
    return rectCoords;
  }, [editor]);
  return computePosition;
};
const OverlayButton = ({
  onClick,
  editor,
  onRemove
}) => {
  const computePosition = useComputeSelectionFloatyPosition(editor);
  const blockEvent = useCallback(evt => {
    // Prevent default to avoid blurring the editor in safari
    // onRemove handler runs if this isn't set
    evt.preventDefault();
  }, []);
  return /*#__PURE__*/_jsx(TinyMCEFloaty, {
    editor: editor,
    target: document.body,
    computePosition: computePosition,
    "data-test-id": "content-assistance-selection",
    onRemove: onRemove,
    content: /*#__PURE__*/_jsx(UIIconButton, {
      onMouseDown: blockEvent,
      className: "mce-content-gen-toolbar",
      onClick: onClick,
      size: "small",
      shape: "circle",
      children: /*#__PURE__*/_jsx(UIIcon, {
        name: "artificialIntelligence",
        style: {
          marginLeft: -2
        }
      })
    })
  });
};
function useBookmark(editor) {
  const [bookmarkId, setBookmarkId] = useState(null);
  const createBookmark = useCallback(() => {
    const bookmark = editor.selection.getBookmark();
    setBookmarkId(bookmark.id);
  }, [editor]);
  const bookmarkEndNode = useMemo(() => {
    if (!bookmarkId) {
      return null;
    }
    return editor.dom.get(`${bookmarkId}_end`);
  }, [bookmarkId, editor]);
  const cleanupBookmark = useCallback(() => {
    if (!bookmarkId) {
      return;
    }
    const startNode = editor.dom.get(`${bookmarkId}_start`);
    const endNode = editor.dom.get(`${bookmarkId}_end`);
    if (startNode) {
      editor.dom.remove(startNode);
    }
    if (endNode) {
      editor.dom.remove(endNode);
    }
    setBookmarkId(null);
  }, [bookmarkId, editor]);
  return {
    bookmarkEndNode,
    createBookmark,
    cleanupBookmark
  };
}
const OverlayContent = ({
  editor,
  selectionCommands,
  onInsert,
  selectedText,
  editorContext,
  ContentAssistanceToolbar,
  onClosePopover,
  setEditorContext,
  bookmarkEndNode,
  createBookmark,
  cleanupBookmark,
  brandVoiceOptions
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const closeOtherInlineTargetPopovers = useCallback(() => editor.fire('hsRemoveInlineTargets'), [editor]);
  const handleTrackInteraction = useCallback((action, props = {}, eventKey = CONTENT_GENERATION_INLINE_EDIT_INTERACTION) => {
    const meta = Object.assign({
      actionType: CONTENT_GENERATION_SELECTION_COMMAND_ACTION_TYPE
    }, props);
    trackInteraction(editor, eventKey, action, meta);
  }, [editor]);

  // Track selection, every time popover is opened (icon is shown).
  useEffect(() => {
    handleTrackInteraction(CONTENT_GENERATION_TEXT_SELECTED);
  }, [handleTrackInteraction]);
  const handleButtonClick = useCallback(() => {
    createBookmark();
    setPopoverOpen(true);
    setEditorContext();
    closeOtherInlineTargetPopovers();
    handleTrackInteraction(CONTENT_GENERATION_MENU_OPEN);
  }, [setEditorContext, closeOtherInlineTargetPopovers, handleTrackInteraction, createBookmark]);
  const handleOpenChange = useCallback(event => {
    if (!event.target.value) {
      setPopoverOpen(false);
      cleanupBookmark();
    }
  }, [cleanupBookmark]);
  const blockEvent = useCallback(evt => {
    // Prevent default to avoid blurring the editor in safari
    // onRemove handler runs if this isn't set
    evt.preventDefault();
  }, []);
  const handlePopoverRemove = useCallback(() => {
    cleanupBookmark();
    onClosePopover();
  }, [cleanupBookmark, onClosePopover]);
  useEffect(() => {
    const handleOpenEvent = () => {
      handleButtonClick();
    };
    editor.on('hscontentgeneration:openSelectionPopover', handleOpenEvent);
    return () => {
      editor.off('hscontentgeneration:openSelectionPopover', handleOpenEvent);
    };
  }, [editor, handleButtonClick]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [!popoverOpen ? /*#__PURE__*/_jsx(OverlayButton, {
      editor: editor,
      onRemove: onClosePopover,
      onClick: handleButtonClick
    }) : null, /*#__PURE__*/_jsx(TinyMcePopover, {
      editor: editor,
      onRemove: handlePopoverRemove,
      placement: "bottom right",
      use: "unstyled",
      autoPlacement: true,
      content: /*#__PURE__*/_jsx("div", {
        onMouseDown: blockEvent,
        style: {
          minWidth: 120,
          minHeight: 165
        },
        children: /*#__PURE__*/_jsx(ContentAssistanceToolbar, {
          brandVoiceOptions: brandVoiceOptions,
          selectedText: selectedText,
          selectionCommands: selectionCommands,
          editorContext: editorContext,
          onTextChange: onInsert,
          onClosePopover: handlePopoverRemove,
          trackInteraction: handleTrackInteraction,
          tooltipClassName: 'mce-content-assistance-inline-toolbar-tooltip'
        })
      }),
      target: bookmarkEndNode,
      onOpenChange: handleOpenChange,
      open: popoverOpen
    })]
  });
};
const useIsEditorActive = editor => {
  const [isActive, setIsActive] = useState('false');
  const activateHandler = useCallback(() => {
    setIsActive(true);
  }, []);
  const deactivateHandler = useCallback(() => {
    setIsActive(false);
  }, []);
  useEffect(() => {
    editor.on('activate', activateHandler);
    editor.on('deactivate', deactivateHandler);
    return () => {
      editor.off('activate', activateHandler);
      editor.off('deactivate', deactivateHandler);
    };
  }, [editor, activateHandler, deactivateHandler]);
  return {
    isActive
  };
};
const useHandlers = editor => {
  const [selectionText, setSelectionText] = useState('');
  const [editorContext, setEditorContext] = useState('');
  const [isMouseDown, setIsMouseDown] = useState(false);
  const updateEditorContext = useCallback(() => {
    const range = editor.selection.getRng();
    const context = getAnnotatedSelectionContextAsPlainText(editor, range);
    setEditorContext(context);
    // Make sure to reset the range for Safari
    editor.selection.setRng(range);
  }, [editor]);
  const {
    onMouseDown,
    onMouseUp,
    handleSelectionChange
  } = useMemo(() => {
    let isExpectingNodeChangeEvent = false;
    function onMouseDownHandler() {
      isExpectingNodeChangeEvent = true;
      setIsMouseDown(true);
    }
    function selectionChangeHandler() {
      isExpectingNodeChangeEvent = false;
      const text = editor.selection.getContent({
        format: 'text'
      });
      const isCollapsed = editor.selection.isCollapsed();
      if (isWhitespace(text) || isCollapsed) {
        setSelectionText('');
        setEditorContext('');
        return;
      }
      setSelectionText(text);
    }
    function onMouseUpHandler() {
      setIsMouseDown(false);
      if (isExpectingNodeChangeEvent) {
        isExpectingNodeChangeEvent = false;
        selectionChangeHandler();
      }
    }
    return {
      onMouseDown: onMouseDownHandler,
      onMouseUp: onMouseUpHandler,
      handleSelectionChange: selectionChangeHandler
    };
  }, [editor]);
  const resetSelection = useCallback(() => {
    setSelectionText('');
    setEditorContext('');
  }, []);
  useEffect(() => {
    editor.on('NodeChange', handleSelectionChange);
    editor.on('mousedown', onMouseDown);
    // Handle mouseup that can occur both on the child frame and on the parent app
    editor.on('mouseup', onMouseUp);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      editor.off('NodeChange', handleSelectionChange);
      editor.off('mousedown', onMouseDown);
      editor.off('mouseup', onMouseUp);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseDown, onMouseUp, handleSelectionChange, editor]);
  return {
    selectionText,
    editorContext,
    setEditorContext: updateEditorContext,
    resetSelection,
    isUpdatingSelectionByMouse: isMouseDown
  };
};
const ContentAssistancePopoverManager = ({
  editor,
  selectionCommands,
  ContentAssistanceToolbar,
  brandVoiceOptions
}) => {
  const {
    selectionText,
    resetSelection,
    editorContext,
    setEditorContext,
    isUpdatingSelectionByMouse
  } = useHandlers(editor);
  const {
    bookmarkEndNode,
    createBookmark,
    cleanupBookmark
  } = useBookmark(editor);
  const handleInsert = useCallback(content => {
    cleanupBookmark();
    editor.undoManager.transact(() => {
      const processedContent = newlinesToParagraphs(content);
      editor.insertContent(processedContent);
    });
  }, [editor, cleanupBookmark]);
  useEffect(() => {
    if (!selectionText) {
      cleanupBookmark();
    }
  }, [selectionText, cleanupBookmark]);
  if (!selectionText || editor.id !== tinymce.activeEditor.id ||
  // Do not show popover if the user is changing the text selection by mouse
  isUpdatingSelectionByMouse) {
    return null;
  }
  return /*#__PURE__*/_jsx(OverlayContent, {
    editor: editor,
    editorContext: editorContext,
    selectionCommands: selectionCommands,
    onInsert: handleInsert,
    selectedText: selectionText,
    onClosePopover: resetSelection,
    ContentAssistanceToolbar: ContentAssistanceToolbar,
    setEditorContext: setEditorContext,
    bookmarkEndNode: bookmarkEndNode,
    createBookmark: createBookmark,
    cleanupBookmark: cleanupBookmark,
    brandVoiceOptions: brandVoiceOptions
  });
};
const ActiveEditorGuard = ({
  editor,
  ContentAssistanceToolbar,
  selectionCommands,
  brandVoiceOptions
}) => {
  const {
    isActive
  } = useIsEditorActive(editor);
  const autocompleteRange = useAtomValue(autocompleteActiveRangeAtom);
  // TinyMCE menu edge case: Don't show if the TinyMCE Content Assistant menu is open
  if (!isActive || autocompleteRange) {
    return null;
  }
  return /*#__PURE__*/_jsx(ContentAssistancePopoverManager, {
    editor: editor,
    ContentAssistanceToolbar: ContentAssistanceToolbar,
    selectionCommands: selectionCommands,
    brandVoiceOptions: brandVoiceOptions
  });
};
export default ActiveEditorGuard;